import { UserProfile } from 'fitbeat.gym-core';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import {
  AppState, StyleSheet, Text, View,
} from 'react-native';
import { connect } from 'react-redux';
import HealthIcon from '../../assets/icons/apple-health.svg';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { AuthorizationStatus } from '../../ConnectedApps/connected-workout-app';
import HealthKit from '../../ConnectedApps/HealthKit/healthkit';
import Theme from '../../theme';
import { showMessage } from '../../ui/components/Toast';
import { defaultScale } from '../../utils/scaling';
import {
  connectAppleHealthKitNextPressed,
  connectAppleHealthKitSkipPressed,
} from '../actions';

interface IState {
  isHealthKitEnabled: boolean;
  appState: string;
}

interface IProps {
  t: (key: string, options?: any) => string;
  userProfile: UserProfile;
  skip: () => void;
  next: () => void;
}

class ConnectAppleHealthKit extends Component<IProps, IState> {
  constructor(props) {
    super(props);
    this.state = {
      isHealthKitEnabled: false,
      appState: AppState.currentState,
    };
  }

  public handleAppStateChange = async (nextState) => {
    this.setState({ appState: nextState });
    await this.setIsHealthKitEnabled();
  };

  public async componentDidMount() {
    await this.setIsHealthKitEnabled();
    if (this.state.isHealthKitEnabled) {
      this.props.next();
    }

    AppState.addEventListener('change', this.handleAppStateChange);
  }

  public componentWillUnmount() {
    AppState.removeEventListener('change', this.handleAppStateChange);
  }

  public setIsHealthKitEnabled = async () => {
    const healthKitStatus = await HealthKit.authorizationStatus();
    this.setState({ isHealthKitEnabled: healthKitStatus === AuthorizationStatus.authorized });
  };

  public requestHealthKit = async () => {
    const { success, errorDisplayMessage } = await HealthKit.requestAuthorization();
    if (!success) {
      showMessage(errorDisplayMessage);
    }
    await this.setIsHealthKitEnabled();
  };

  public render() {
    const { isHealthKitEnabled } = this.state;
    const { t, skip, next } = this.props;

    return (
      <View style={styles.container}>
        <View style={styles.contentContainer}>
          <View style={styles.imageContainer}>
            <HealthIcon
              style={styles.healthIcon}
              width={defaultScale(68)}
              height={defaultScale(68)}
            />
          </View>
          <View>
            {!isHealthKitEnabled ? (
              <>
                <Text style={styles.heading}>{t('connectHealth')}</Text>
                <Text style={styles.body}>{t('allowAccessToHealth')}</Text>
              </>
            ) : (
              <Text style={styles.heading}>{t('connectedToHealth')}</Text>
            )}
          </View>
        </View>
        <View>
          {!isHealthKitEnabled ? (
            <>
              <Button
                fontColor={Theme.WHITE}
                size={ButtonSize.MEDIUM}
                title={t('connect')}
                onPress={this.requestHealthKit}
              />
              <Button
                fontColor={Theme.PRIMARY}
                size={ButtonSize.MEDIUM}
                buttonType={ButtonTypes.TRANSPARENT}
                title={t('skip')}
                onPress={skip}
              />
            </>
          ) : (
            <Button
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              title={t('continue')}
              onPress={next}
            />
          )}
        </View>
      </View>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps: IProps): Partial<IProps> => {
  const { userProfile } = ownProps;
  return {
    skip: () => dispatch(connectAppleHealthKitSkipPressed(userProfile)),
    next: () => dispatch(connectAppleHealthKitNextPressed(userProfile)),
  };
};

export const ConnectAppleHealthKitStep = connect(null, mapDispatchToProps)(
  translate(['bookings'], { wait: true })(ConnectAppleHealthKit),
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  imageContainer: {
    flex: 0.15,
    display: 'flex',
    alignItems: 'center',
  },
  heading: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
    marginVertical: defaultScale(10),
  },
  body: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
    marginVertical: defaultScale(10),
  },
  healthIcon: {
    shadowOffset: { width: 0, height: 2 },
    shadowColor: Theme.BLACK,
    shadowRadius: 3,
    shadowOpacity: 0.1,
  },
});
